import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react';
import Cookies from 'js-cookie';
import { FuseAuthProviderComponentType, FuseAuthProviderState } from '@fuse/core/FuseAuthProvider/types/FuseAuthTypes';
import { authRefreshToken, authSignOut, authSignIn, authValidate, authSignUp, authUpdateDbUser } from '@auth/authApi';
import { User } from '../../user';
import { setGlobalHeaders, removeGlobalHeaders, API_BASE_URL } from '@/utils/apiFetch';
import { getOpeningPagePath } from '@/configs/openingPageConfig';

export type JwtSignInPayload = {
  email: string;
  password: string;
};

export type JwtSignUpPayload = {
  displayName: string;
  email: string;
  password: string;
};

export type JwtAuthContextType = FuseAuthProviderState<User> & {
  updateUser: (U: User) => Promise<Response>;
  signIn?: (credentials: JwtSignInPayload) => Promise<Response>;
  signUp?: (U: JwtSignUpPayload) => Promise<Response>;
  signOut?: () => void;
  refreshToken?: () => Promise<string | Response>;
};

const defaultAuthContext: JwtAuthContextType = {
  authStatus: 'configuring',
  isAuthenticated: false,
  user: null,
  updateUser: null,
  signIn: null,
  signUp: null,
  signOut: null,
  refreshToken: null
};

export const JwtAuthContext = createContext<JwtAuthContextType>(defaultAuthContext);

const JwtAuthProvider: FuseAuthProviderComponentType = forwardRef(({ children, onAuthStateChanged }, ref) => {
  /**
   * Auth Provider State
   */
  const [authState, setAuthState] = useState<FuseAuthProviderState<User>>({
    authStatus: 'configuring',
    isAuthenticated: false,
    user: null
  });

  /**
   * Guards against an expired/invalid session firing signOut() once per in-flight
   * request. When several protected calls 401 at the same time (e.g. an archive page
   * firing multiple queries), we only want to clear auth and redirect a single time.
   * Reset on a successful sign-in.
   */
  const signingOutRef = React.useRef(false);

  /**
   * Watch for changes in the auth state
   * and pass them to the FuseAuthProvider
   */
  useEffect(() => {
    if (onAuthStateChanged) {
      onAuthStateChanged(authState);
    }
  }, [authState, onAuthStateChanged]);

  /**
   * Attempt to auto login with the stored token
   */
  useEffect(() => {
    const attemptAutoLogin = async () => {
      try {
        // ✅ Retrieve the token from localStorage first, then cookies as fallback
        const token = localStorage.getItem('jwt_token') || Cookies.get('jwt_token');
        const settings = JSON.parse(localStorage.getItem('settings'));

        if (!token) {
          // ✅ Explicitly set the state to unauthenticated so UI updates
          setAuthState({
            authStatus: 'unauthenticated',
            isAuthenticated: false,
            user: null
          });

          return; // ✅ Exit early to prevent unnecessary API calls
        }

        // ✅ Set token in global headers BEFORE validation
        setGlobalHeaders({ Authorization: `Bearer ${token}` });

        // ✅ Validate the token
        const response = await authValidate();
        const userData = response;

        if (!userData || !userData.email) {
          throw new Error('Invalid user data from token validation.');
        }

        if (!settings?.theme) {
          try {
            const { defaultSettings } = await import('@fuse/default-settings');
            userData.settings = defaultSettings;
            // ✅ Store user settings for consistency
            localStorage.setItem('settings', JSON.stringify(userData.settings));
          } catch (err) {
            console.error('Failed to initialize default settings:', err);
          }
        }

        // A validation started before sign-out must not restore the session.
        if (signingOutRef.current) return;

        // ✅ Update auth state properly
        setAuthState({
          authStatus: 'authenticated',
          isAuthenticated: true,
          user: userData
        });

        // ✅ Redirect to the user's opening page (Account settings; dashboard by default)
        if (window.location.pathname === '/signin' && authState.isAuthenticated) {
          window.location.href = getOpeningPagePath(userData?.openingPage);
        }
      } catch (error) {
        console.error('❌ Auto-login failed:', error);

        // ❌ Clear invalid tokens to prevent infinite loop
        Cookies.remove('jwt_token');
        localStorage.removeItem('jwt_token');
        // localStorage.removeItem('settings');

        setAuthState({
          authStatus: 'unauthenticated',
          isAuthenticated: false,
          user: null
        });
      }
    };

    // ✅ Prevent auto-login if already authenticated
    if (!authState.isAuthenticated) {
      attemptAutoLogin();
    }
  }, [authState.isAuthenticated]);

  /**
   * Sign in
   */
  const signIn: JwtAuthContextType['signIn'] = async (credentials) => {
    try {
      const responseData = await authSignIn(credentials);

      const { user, access_token } = responseData;
      const settings = JSON.parse(localStorage.getItem('settings'));

      if (!access_token) {
        throw new Error('No access token returned from the server');
      }

      if (!settings?.theme) {
        try {
          const { defaultSettings } = await import('@fuse/default-settings');

          if (!defaultSettings || Object.keys(defaultSettings).length === 0) {
            console.error('Warning: defaultSettings is empty!');
          } else {
            user.settings = defaultSettings;
            localStorage.setItem('settings', JSON.stringify(user.settings));
          }
        } catch (err) {
          console.error('Failed to initialize default settings:', err);
        }
      }

      // ✅ Store token in localStorage for persistence (used for the Authorization header)
      localStorage.setItem('jwt_token', access_token);

      // ✅ Set token immediately for API requests
      setGlobalHeaders({ Authorization: `Bearer ${access_token}` });

      // NOTE: the jwt_token cookie is set by the backend as HttpOnly/Secure and is the
      // browser-auto-sent auth credential. We deliberately do NOT mirror it from JS — doing
      // so would re-create a non-HttpOnly cookie readable by any XSS. Auth requests use the
      // Authorization header (above) plus the auto-sent HttpOnly cookie.

      cleanupExpiredStorage();

      // ✅ Reset the sign-out guard
      signingOutRef.current = false;

      // ✅ Set authentication state BEFORE redirect
      setAuthState({
        authStatus: 'authenticated',
        isAuthenticated: true,
        user
      });

      // ✅ Redirect to the user's opening page (Account settings; dashboard by default).
      // Full navigation on purpose: the app re-validates the token and reloads settings on boot.
      window.location.href = getOpeningPagePath(user?.openingPage);

      return responseData;
    } catch (error) {
      console.error('Error during signIn:', error);
      throw error;
    }
  };

  const cleanupExpiredStorage = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key) {
        const itemStr = localStorage.getItem(key);

        if (itemStr) {
          try {
            const item = JSON.parse(itemStr) as { expiry?: number };

            if (item.expiry && new Date().getTime() > item.expiry) {
              localStorage.removeItem(key); // Remove expired item
            }
          } catch {
            // Ignore parsing errors for non-expiring items
          }
        }
      }
    }
  };

  /**
   * Sign up
   */
  const signUp: JwtAuthContextType['signUp'] = async (data) => {
    const response = await authSignUp(data);
    const session = await response.json() as { user: User; access_token: string };

    if (session) {
      setAuthState({
        authStatus: 'authenticated',
        isAuthenticated: true,
        user: session.user
      });
      // jwt_token is an HttpOnly cookie set by the backend — never mirror it from JS (would
      // re-expose it to XSS). The Authorization header below covers API calls.
      if (session.access_token) {
        localStorage.setItem('jwt_token', session.access_token);
        setGlobalHeaders({ Authorization: `Bearer ${session.access_token}` });
      }
    }

    return response;
  };

  // Get the root domain from current location
  const getRootDomain = () => {
    const { hostname } = window.location;

    // For localhost or IP addresses, don't set domain
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return undefined; // No domain restriction for local development
    }

    // For domains like 'app.example.com' or 'example.com'
    const parts = hostname.split('.');

    if (parts.length >= 2) {
      return `.${parts.slice(-2).join('.')}`; // Returns '.example.com'
    }

    return `.${hostname}`;
  };

  /**
   * Sign out
   */
  const signOut: JwtAuthContextType['signOut'] = useCallback(async () => {
    // Manual logout and concurrent 401s must share the same logout operation.
    if (signingOutRef.current) return;
    signingOutRef.current = true;

    try {
      await authSignOut();
    } catch (error) {
      console.error('❌ Error during signOut:', error);
    }

    // ✅ Clear all authentication data
    localStorage.removeItem('jwt_token');
    // NOTE: deliberately keep 'settings' in localStorage across sign-out. It holds only UI layout
    // preferences (navbar/toolbar style, theme), not auth data, and it is the de-facto per-browser
    // store the layout reads on startup. Removing it here meant a sign-out or session-expiry (401 →
    // signOut) wiped the user's chosen layout, which then reseeded to defaults on next login.
    // Remove both legacy host-only and shared-domain JS cookies. The API clears
    // the HttpOnly session cookie; JavaScript cannot remove that cookie itself.
    Cookies.remove('jwt_token');
    Cookies.remove('jwt_token', { domain: getRootDomain() });

    // ✅ Clear global headers
    removeGlobalHeaders(['Authorization']);

    // Cross the session boundary with a fresh document, as sign-in already does.
    // Keep login independent of outgoing DOM/style state after the reported
    // Emotion stylesheet insertion crash. Do not mount login in the current
    // React tree while navigation is pending.
    window.location.replace('/sign-in');
  }, []);

  /**
   * Update user
   */
  const updateUser: JwtAuthContextType['updateUser'] = useCallback(async (_user) => {
    try {
      return await authUpdateDbUser(_user);
    } catch (error) {
      console.error('Error updating user:', error);
      return Promise.reject(error);
    }
  }, []);

  /**
   * Refresh access token
   */
  const refreshToken: JwtAuthContextType['refreshToken'] = async () => {
    const response = await authRefreshToken();

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    return response;
  };

  /**
   * Auth Context Value
   */
  const authContextValue = useMemo(
    () =>
      ({
        ...authState,
        signIn,
        signUp,
        signOut,
        updateUser,
        refreshToken
      }) as JwtAuthContextType,
    [authState, signIn, signUp, signOut, updateUser, refreshToken]
  );

  /**
   * Expose methods to the FuseAuthProvider
   */
  useImperativeHandle(ref, () => ({
    signOut,
    updateUser
  }));

  /**
   * Intercept fetch requests to refresh the access token
   */
  const interceptFetch = useCallback(() => {
    const { fetch: originalFetch } = window;

    // Only trust auth signals (token rotation, 401 sign-out) from responses to OUR OWN API
    // origin. The interceptor wraps every window.fetch, including third-party calls (maps,
    // Syncfusion, etc.); without this check any external endpoint could inject a
    // New-Access-Token header and overwrite the session token.
    let apiOrigin = '';
    try {
      apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
    } catch {
      apiOrigin = '';
    }

    window.fetch = async (...args) => {
      const [resource, config] = args;
      const response = await originalFetch(resource, config);

      let isOurApi = false;
      try {
        isOurApi = !!response.url && new URL(response.url).origin === apiOrigin;
      } catch {
        isOurApi = false;
      }

      if (!isOurApi || signingOutRef.current) {
        return response;
      }

      const newAccessToken = response.headers.get('New-Access-Token');

      if (newAccessToken) {
        setGlobalHeaders({ Authorization: `Bearer ${newAccessToken}` });
        // Refresh the localStorage copy used for the Authorization header. The HttpOnly
        // jwt_token cookie is refreshed by the backend; JS must not write it (see signIn).
        localStorage.setItem('jwt_token', newAccessToken);
      }

      if (response.status === 401 && !signingOutRef.current) {
        // Expired/invalid session: clear auth and redirect to login exactly once,
        // not on every concurrent request that comes back 401.
        console.warn('Session expired or unauthorized — signing out.');
        signOut();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [signOut]);

  useEffect(() => {
    if (authState.isAuthenticated) {
      return interceptFetch();
    }
  }, [authState.isAuthenticated, interceptFetch]);

  return <JwtAuthContext.Provider value={authContextValue}>{children}</JwtAuthContext.Provider>;
});

export default JwtAuthProvider;
